import { Fragment, useRef } from "react";

import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { Failure } from "@vega-core/components/StatusPopup/components/Failure";
import { Success } from "@vega-core/components/StatusPopup/components/Success";
import { useTheme } from "@vega-core/context/ThemeContext/ThemeContext";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isTruthy } from "@vega-core/utils/isTruthy";

export const StatusPopup = ({
  description,
  onClose,
  open,
  title,
  type,
}: {
  description?: string;
  onClose: () => void;
  open: boolean;
  title?: string;
  type: "error" | "success" | null;
}) => {
  const { theme } = useTheme();
  const cancelButtonRef = useRef(null);

  return (
    <Transition show={open}>
      <Dialog
        aria-label="modal"
        as="div"
        className={cnTwMerge("relative z-50 text-text/primary", theme)}
        data-testid="status-popup-dialog"
        initialFocus={cancelButtonRef}
        onClose={onClose}
      >
        <TransitionChild
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-background/primary/[0.9] transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-center justify-center min-h-full text-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel className="relative overflow-hidden transform bg-transparent rounded-lg">
                <>
                  {type === "error" && open ? (
                    <Failure
                      description={description}
                      firstAction={onClose}
                      firstActionTitle={m.btn_close()}
                      title={isTruthy(title) ? title : m.status_error()}
                    />
                  ) : null}
                  {type === "success" && open ? (
                    <Success
                      description={description}
                      firstAction={onClose}
                      firstActionTitle={m.btn_dismiss()}
                      title={isTruthy(title) ? title : m.status_error()}
                    />
                  ) : null}
                </>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
